/**
 * @lodex-pro/web-portal/public/DisposalRegisterPage — disposal-company self-registration form.
 *
 * DEC-160 — closes the frontend gap for the disposal-portal register flow.
 * Backend endpoints (register + magic-link/issue + magic-link/consume) shipped
 * in Phase 16 / Task 16.1 (DEC-156). Full-copy of RegisterPage.tsx (DEC-155)
 * adapted for the 8-field disposal payload + SWD payment-activation notice.
 *
 * Flow:
 *   1. User fills form (companyName, contactName, email, phone, state — required;
 *      licenseNumber, facilityName, facilityAddress — optional).
 *   2. Turnstile widget renders below form; submit button disabled until solved.
 *   3. On valid form + captchaToken: POST /v1/disposal-portal/register.
 *      Empty-string optionals are stripped (react-hook-form sends `''` by
 *      default; backend Zod expects `undefined` for omitted optionals).
 *   4. On 201: reset Turnstile widget (fresh token needed for /issue — tokens
 *      are single-use per Cloudflare docs; mirrors DEC-155 grey area 1).
 *   5. Widget re-solves.
 *   6. When fresh token arrives: POST /v1/disposal-portal/magic-link/issue.
 *   7. On 202 (or any error — issue is best-effort): show <DisposalCheckYourEmail>
 *      with the licenseMatchStatus from the register response so the confirmation
 *      copy is tailored to MATCHED / UNMATCHED / NOT_PROVIDED.
 *
 * ?ref={hauler_slug}: cosmetic display only, mirrors operator flow. Not sent to backend.
 *
 * Touch-target compliance (CLAUDE.md): inputs min-height 52px, button min-height 56px,
 * body text 16px.
 *
 * Spec: DEC-147 (UPPERCASE-on-write — server-side enforcement so no client-side
 *  transformation needed here), DEC-154, DEC-155, DEC-156, DEC-160; master spec §49, §50, §81.
 */

import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { useSearchParams } from 'react-router-dom';
import { useDisposalRegisterMutation } from '../hooks/useDisposalRegisterMutation';
import { useDisposalMagicLinkIssueMutation } from '../hooks/useDisposalMagicLinkIssueMutation';
import { PublicApiError } from './publicApi';
import { DisposalCheckYourEmail } from './DisposalCheckYourEmail';

// ---------------------------------------------------------------------------
// Form schema — mirrors DisposalRegistrationRequest shape (DEC-156).
// Inline here (no runtime import of the shared Zod schema) to keep the web
// bundle lean; this is a local UI-only validation spec. The 5-state enum is
// duplicated inline for the same reason.
// ---------------------------------------------------------------------------

const DISPOSAL_STATES = ['OK', 'TX', 'NM', 'WY', 'ND'] as const;

const STATE_LABELS: Record<(typeof DISPOSAL_STATES)[number], string> = {
  OK: 'Oklahoma',
  TX: 'Texas',
  NM: 'New Mexico',
  WY: 'Wyoming',
  ND: 'North Dakota',
};

// Empty string treated as omitted for optional fields (react-hook-form sends
// `''` by default; we strip before POST so the backend Zod schema sees `undefined`).
const optionalIdentifier = (max: number, label: string) =>
  z
    .string()
    .trim()
    .max(max, `${label} must be at most ${max} characters.`)
    .optional()
    .or(z.literal(''));

const registerSchema = z.object({
  companyName: z.string().trim().min(2, 'Company name must be at least 2 characters.').max(200),
  contactName: z.string().trim().min(2, 'Contact name must be at least 2 characters.').max(200),
  email: z.string().trim().email('Enter a valid email address.').max(320),
  phone: z.string().trim().min(7, 'Enter a valid phone number.').max(50),
  state: z.enum(DISPOSAL_STATES, {
    errorMap: () => ({ message: 'Select a state.' }),
  }),
  licenseNumber: optionalIdentifier(50, 'License number'),
  facilityName: optionalIdentifier(200, 'Facility name'),
  facilityAddress: optionalIdentifier(500, 'Facility address'),
});

type RegisterFormValues = z.infer<typeof registerSchema>;

/**
 * Convert `''` to `undefined` for optional fields so the backend Zod schema
 * (which uses `.optional()` without `.or(z.literal(''))`) treats the field as
 * omitted rather than as an empty string.
 */
function stripEmptyOptional(value: string | undefined): string | undefined {
  if (value === undefined) return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

export function DisposalRegisterPage(): React.JSX.Element {
  const [searchParams] = useSearchParams();
  const refSlug = searchParams.get('ref');

  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  // Register→issue chain: after register succeeds, we reset the widget and
  // wait for a fresh token before firing /issue. pendingIssueEmail tracks
  // that we're in the "awaiting second token" state.
  const [pendingIssueEmail, setPendingIssueEmail] = useState<string | null>(null);

  // Terminal success state — swap to DisposalCheckYourEmail. We hold the
  // licenseMatchStatus alongside the verified email so the confirmation copy
  // can render the matched-facility / manual-verification / no-license text.
  const [verifiedState, setVerifiedState] = useState<{
    email: string;
    licenseMatchStatus: 'MATCHED' | 'UNMATCHED' | 'NOT_PROVIDED';
  } | null>(null);

  const turnstileRef = useRef<TurnstileInstance>(null);

  const registerMutation = useDisposalRegisterMutation();
  const issueMutation = useDisposalMagicLinkIssueMutation();

  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      companyName: '',
      contactName: '',
      email: '',
      phone: '',
      state: undefined as unknown as (typeof DISPOSAL_STATES)[number],
      licenseNumber: '',
      facilityName: '',
      facilityAddress: '',
    },
    mode: 'onTouched',
  });

  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

  // Fires when Turnstile succeeds — either initial solve or after reset.
  const handleCaptchaSuccess = (token: string): void => {
    setCaptchaToken(token);

    // If we're in the "awaiting second token" state (register already succeeded),
    // fire /issue now with the fresh token.
    if (pendingIssueEmail !== null) {
      const email = pendingIssueEmail;
      setPendingIssueEmail(null);

      issueMutation.mutate(
        { email, captchaToken: token },
        {
          onSuccess: () => {
            // Terminal state already includes licenseMatchStatus from register
            // response, which we captured into verifiedState via the setter
            // below during registerMutation.onSuccess. Re-affirm here by
            // reading from the (frozen at register time) closure state.
            //
            // Actually — we set verifiedState _here_ so the transition happens
            // after issue completes. Read licenseMatchStatus from the register
            // mutation's data (it's still available on the mutation object).
            const licenseMatchStatus = registerMutation.data?.licenseMatchStatus ?? 'NOT_PROVIDED';
            setVerifiedState({ email, licenseMatchStatus });
          },
          onError: () => {
            // Issue is best-effort — even on failure, show DisposalCheckYourEmail
            // (user can use the resend button there).
            const licenseMatchStatus = registerMutation.data?.licenseMatchStatus ?? 'NOT_PROVIDED';
            setVerifiedState({ email, licenseMatchStatus });
          },
        },
      );
    }
  };

  const onSubmit = (values: RegisterFormValues): void => {
    if (captchaToken === null) return;
    setBanner(null);

    // Empty-string optionals must be stripped BEFORE POST so the backend Zod
    // schema (`.optional()`) treats them as omitted rather than as `''`.
    registerMutation.mutate(
      {
        companyName: values.companyName,
        contactName: values.contactName,
        email: values.email,
        phone: values.phone,
        state: values.state,
        licenseNumber: stripEmptyOptional(values.licenseNumber),
        facilityName: stripEmptyOptional(values.facilityName),
        facilityAddress: stripEmptyOptional(values.facilityAddress),
        captchaToken,
      },
      {
        onSuccess: () => {
          // Register succeeded. Reset widget for fresh token.
          setCaptchaToken(null);
          setPendingIssueEmail(values.email);
          turnstileRef.current?.reset();
          // handleCaptchaSuccess will fire with the fresh token and call /issue.
        },
        onError: (err) => {
          setCaptchaToken(null);
          turnstileRef.current?.reset();

          if (err instanceof PublicApiError) {
            if (err.code === 'VALIDATION_FAILED' && err.fieldErrors !== undefined) {
              const knownFields: ReadonlyArray<keyof RegisterFormValues> = [
                'companyName',
                'contactName',
                'email',
                'phone',
                'state',
                'licenseNumber',
                'facilityName',
                'facilityAddress',
              ];
              for (const [field, message] of Object.entries(err.fieldErrors)) {
                const fieldName = field as keyof RegisterFormValues;
                if (knownFields.includes(fieldName)) {
                  form.setError(fieldName, { message });
                }
              }
              return;
            }
            if (err.code === 'CAPTCHA_REQUIRED' || err.code === 'CAPTCHA_VERIFICATION_FAILED') {
              setBanner('Please complete the security check and try again.');
              return;
            }
            if (err.code === 'RATE_LIMIT_EXCEEDED') {
              const seconds = err.retryAfterSeconds;
              setBanner(
                seconds !== undefined
                  ? `Too many attempts. Try again in ${seconds} seconds.`
                  : 'Too many attempts. Try again shortly.',
              );
              return;
            }
          }
          setBanner('Something went wrong. Try again.');
        },
      },
    );
  };

  // Terminal state — show DisposalCheckYourEmail after the chain completes.
  if (verifiedState !== null) {
    return (
      <DisposalCheckYourEmail
        email={verifiedState.email}
        licenseMatchStatus={verifiedState.licenseMatchStatus}
      />
    );
  }

  const isSubmitting =
    registerMutation.isPending || issueMutation.isPending || pendingIssueEmail !== null;

  const inputStyle: React.CSSProperties = {
    width: '100%',
    minHeight: '52px',
    padding: '12px',
    background: 'var(--color-bg-tertiary)',
    color: 'var(--color-text-primary)',
    border: '1px solid var(--color-border-secondary)',
    borderRadius: '10px',
    fontSize: '16px',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '14px',
    marginBottom: '6px',
    color: 'var(--color-text-secondary)',
  };

  return (
    <div>
      <h1
        style={{
          fontSize: '22px',
          fontWeight: 700,
          marginBottom: '4px',
          color: 'var(--color-text-primary)',
        }}
      >
        Create a disposal-company account
      </h1>

      {refSlug !== null && (
        <p
          style={{
            fontSize: '13px',
            color: 'var(--color-text-tertiary)',
            marginBottom: '16px',
            marginTop: '4px',
          }}
        >
          Referred by {refSlug}
        </p>
      )}

      {/* SWD payment-activation notice (§49) — set expectations up front:
          registration creates a PAYMENT_PENDING account; portal access begins
          after payment activation. Avoids "why can't I see anything after
          approval" support tickets. */}
      <p
        role="note"
        style={{
          background: 'rgba(246, 182, 94, 0.14)',
          borderLeft: '3px solid var(--color-warning)',
          padding: '12px 16px',
          marginBottom: '20px',
          color: 'var(--color-warning)',
          fontSize: '14px',
          lineHeight: '1.5',
        }}
      >
        <strong>SWD portal activation is a two-step process.</strong> After Paelode admin approves
        your registration, a representative will contact you to complete billing setup. Portal
        access begins after payment activation.
      </p>

      {banner !== null && (
        <p
          role="alert"
          style={{
            color: 'var(--color-danger)',
            fontSize: '14px',
            marginBottom: '12px',
            padding: '10px',
            background: 'rgba(243, 139, 139, 0.1)',
            borderRadius: '12px',
            border: '1px solid rgba(243, 139, 139, 0.4)',
          }}
        >
          {banner}
        </p>
      )}

      {pendingIssueEmail !== null && (
        <p
          role="status"
          style={{ color: 'var(--color-link)', fontSize: '14px', marginBottom: '12px' }}
        >
          Sending verification email… Please complete the security check below.
        </p>
      )}

      <form
        onSubmit={(e) => {
          void form.handleSubmit(onSubmit)(e);
        }}
        aria-label="Disposal-company registration"
        noValidate
      >
        {/* Company name */}
        <div style={{ marginBottom: '16px' }}>
          <label htmlFor="disp-reg-company" style={labelStyle}>
            Company name *
          </label>
          <input
            id="disp-reg-company"
            type="text"
            autoComplete="organization"
            {...form.register('companyName')}
            style={inputStyle}
          />
          {form.formState.errors.companyName !== undefined && (
            <p
              role="alert"
              style={{ color: 'var(--color-danger)', fontSize: '13px', marginTop: '4px' }}
            >
              {form.formState.errors.companyName.message}
            </p>
          )}
        </div>

        {/* Contact name */}
        <div style={{ marginBottom: '16px' }}>
          <label htmlFor="disp-reg-contact" style={labelStyle}>
            Contact name *
          </label>
          <input
            id="disp-reg-contact"
            type="text"
            autoComplete="name"
            {...form.register('contactName')}
            style={inputStyle}
          />
          {form.formState.errors.contactName !== undefined && (
            <p
              role="alert"
              style={{ color: 'var(--color-danger)', fontSize: '13px', marginTop: '4px' }}
            >
              {form.formState.errors.contactName.message}
            </p>
          )}
        </div>

        {/* Email */}
        <div style={{ marginBottom: '16px' }}>
          <label htmlFor="disp-reg-email" style={labelStyle}>
            Email address *
          </label>
          <input
            id="disp-reg-email"
            type="email"
            autoComplete="email"
            {...form.register('email')}
            style={inputStyle}
          />
          {form.formState.errors.email !== undefined && (
            <p
              role="alert"
              style={{ color: 'var(--color-danger)', fontSize: '13px', marginTop: '4px' }}
            >
              {form.formState.errors.email.message}
            </p>
          )}
        </div>

        {/* Phone */}
        <div style={{ marginBottom: '20px' }}>
          <label htmlFor="disp-reg-phone" style={labelStyle}>
            Phone number *
          </label>
          <input
            id="disp-reg-phone"
            type="tel"
            autoComplete="tel"
            {...form.register('phone')}
            style={inputStyle}
          />
          {form.formState.errors.phone !== undefined && (
            <p
              role="alert"
              style={{ color: 'var(--color-danger)', fontSize: '13px', marginTop: '4px' }}
            >
              {form.formState.errors.phone.message}
            </p>
          )}
        </div>

        {/* Section divider — facility details */}
        <div
          style={{
            borderTop: '1px solid var(--color-border-primary)',
            marginTop: '8px',
            marginBottom: '16px',
            paddingTop: '16px',
          }}
        >
          <h2
            style={{
              fontSize: '15px',
              fontWeight: 600,
              color: 'var(--color-text-tertiary)',
              margin: 0,
            }}
          >
            Facility details
          </h2>
        </div>

        {/* State (required) — native <select>, 5 options */}
        <div style={{ marginBottom: '16px' }}>
          <label htmlFor="disp-reg-state" style={labelStyle}>
            State *
          </label>
          <select
            id="disp-reg-state"
            {...form.register('state')}
            style={inputStyle}
            defaultValue=""
          >
            <option value="" disabled>
              Select a state…
            </option>
            {DISPOSAL_STATES.map((code) => (
              <option key={code} value={code}>
                {STATE_LABELS[code]} ({code})
              </option>
            ))}
          </select>
          {form.formState.errors.state !== undefined && (
            <p
              role="alert"
              style={{ color: 'var(--color-danger)', fontSize: '13px', marginTop: '4px' }}
            >
              {form.formState.errors.state.message}
            </p>
          )}
        </div>

        {/* License number (optional) */}
        <div style={{ marginBottom: '16px' }}>
          <label htmlFor="disp-reg-license" style={labelStyle}>
            State license / permit number (optional)
          </label>
          <input
            id="disp-reg-license"
            type="text"
            autoComplete="off"
            {...form.register('licenseNumber')}
            style={inputStyle}
          />
          {form.formState.errors.licenseNumber !== undefined && (
            <p
              role="alert"
              style={{ color: 'var(--color-danger)', fontSize: '13px', marginTop: '4px' }}
            >
              {form.formState.errors.licenseNumber.message}
            </p>
          )}
        </div>

        {/* Facility name (optional) */}
        <div style={{ marginBottom: '16px' }}>
          <label htmlFor="disp-reg-facility-name" style={labelStyle}>
            Facility name (optional)
          </label>
          <input
            id="disp-reg-facility-name"
            type="text"
            autoComplete="off"
            {...form.register('facilityName')}
            style={inputStyle}
          />
          {form.formState.errors.facilityName !== undefined && (
            <p
              role="alert"
              style={{ color: 'var(--color-danger)', fontSize: '13px', marginTop: '4px' }}
            >
              {form.formState.errors.facilityName.message}
            </p>
          )}
        </div>

        {/* Facility address (optional) */}
        <div style={{ marginBottom: '20px' }}>
          <label htmlFor="disp-reg-facility-address" style={labelStyle}>
            Facility address (optional)
          </label>
          <input
            id="disp-reg-facility-address"
            type="text"
            autoComplete="street-address"
            {...form.register('facilityAddress')}
            style={inputStyle}
          />
          {form.formState.errors.facilityAddress !== undefined && (
            <p
              role="alert"
              style={{ color: 'var(--color-danger)', fontSize: '13px', marginTop: '4px' }}
            >
              {form.formState.errors.facilityAddress.message}
            </p>
          )}
        </div>

        {/* Turnstile widget */}
        <div style={{ marginBottom: '20px' }}>
          <Turnstile
            ref={turnstileRef}
            siteKey={siteKey}
            onSuccess={handleCaptchaSuccess}
            onError={() => {
              setCaptchaToken(null);
            }}
            onExpire={() => {
              setCaptchaToken(null);
            }}
            options={{ theme: 'dark' }}
          />
        </div>

        <button
          type="submit"
          disabled={!form.formState.isValid || captchaToken === null || isSubmitting}
          style={{
            width: '100%',
            minHeight: '56px',
            padding: '14px 24px',
            background:
              form.formState.isValid && captchaToken !== null && !isSubmitting
                ? 'var(--color-accent-blue)'
                : 'var(--color-bg-tertiary)',
            color:
              form.formState.isValid && captchaToken !== null && !isSubmitting
                ? 'var(--color-on-accent)'
                : 'var(--color-text-tertiary)',
            border: 'none',
            borderRadius: 'var(--radius-pill)',
            fontSize: '16px',
            fontWeight: 600,
            cursor:
              form.formState.isValid && captchaToken !== null && !isSubmitting
                ? 'pointer'
                : 'not-allowed',
          }}
        >
          {isSubmitting ? 'Creating account…' : 'Create account'}
        </button>
      </form>
    </div>
  );
}
